import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  CreditCard, 
  Shield, 
  AlertTriangle, 
  Calendar,
  Clock,
  Plus,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { format, parseISO, differenceInDays } from 'date-fns';

const DashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const config = { headers, withCredentials: true };
        
        const [statsRes, renewalsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, config),
          axios.get(`${API}/renewals`, config)
        ]);
        
        setStats(statsRes.data);
        setRenewals(renewalsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const getStatusBadge = (renewalDate) => {
    const days = differenceInDays(parseISO(renewalDate), new Date());
    if (days < 0) return <Badge variant="destructive">Expired</Badge>;
    if (days <= 7) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Due in {days} days</Badge>;
    if (days <= 30) return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Due in {days} days</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{days} days left</Badge>;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'document': return <FileText className="w-4 h-4" />;
      case 'subscription': return <CreditCard className="w-4 h-4" />;
      case 'insurance': return <Shield className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const upcomingRenewals = renewals
    .filter(r => differenceInDays(parseISO(r.renewal_date), new Date()) >= 0)
    .slice(0, 5);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-ink font-display">
              Welcome back, {user?.name?.split(' ')[0]}.
            </h1>
            <p className="text-ink-600 mt-1">Here's what's on the horizon.</p>
          </div>
          <Link to="/renewals">
            <Button className="bg-ink hover:bg-black text-white" data-testid="add-renewal-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Renewal
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-ink-600 uppercase tracking-wide">Total Renewals</p>
                  <p className="text-3xl font-bold text-ink mt-1">{stats?.total_renewals || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-md bg-cream flex items-center justify-center">
                  <FileText className="w-6 h-6 text-ink" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-ink-600 uppercase tracking-wide">Due This Week</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{stats?.upcoming_7days || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-md bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-ink-600 uppercase tracking-wide">Due This Month</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.upcoming_30days || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-md bg-amber-50 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-ink-600 uppercase tracking-wide">Expired</p>
                  <p className="text-3xl font-bold text-ink mt-1">{stats?.expired || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-md bg-cream flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-ink-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Upcoming Renewals */}
          <div className="lg:col-span-2">
            <Card className="bg-white border border-ink-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-semibold text-ink font-display">
                  Upcoming Renewals
                </CardTitle>
                <Link to="/renewals">
                  <Button variant="ghost" size="sm" className="text-respondent hover:text-respondent hover:bg-cream">
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent className="p-0">
                {upcomingRenewals.length === 0 ? (
                  <div className="p-6 text-center text-ink-600">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-ink-600/50" />
                    <p>Nothing on the calendar yet.</p>
                    <Link to="/renewals">
                      <Button variant="link" className="text-respondent mt-2">Add your first renewal</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-ink-200">
                    {upcomingRenewals.map((renewal) => (
                      <div key={renewal.renewal_id} className="p-4 hover:bg-paper transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                              renewal.category === 'document' ? 'bg-cream text-respondent' :
                              renewal.category === 'subscription' ? 'bg-emerald-50 text-emerald-600' :
                              'bg-amber-50 text-amber-600'
                            }`}>
                              {getCategoryIcon(renewal.category)}
                            </div>
                            <div>
                              <p className="font-medium text-ink">{renewal.title}</p>
                              <p className="text-sm text-ink-600 capitalize">{renewal.category}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(renewal.renewal_date)}
                            <p className="text-xs text-ink-600 mt-1">
                              {format(parseISO(renewal.renewal_date), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdown */}
          <div>
            <Card className="bg-white border border-ink-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-ink font-display">
                  By Category
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'document', label: 'Documents', icon: FileText, color: 'blue' },
                  { key: 'subscription', label: 'Subscriptions', icon: CreditCard, color: 'emerald' },
                  { key: 'insurance', label: 'Insurance', icon: Shield, color: 'amber' }
                ].map((cat) => (
                  <div key={cat.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-${cat.color}-50`}>
                        <cat.icon className={`w-5 h-5 text-${cat.color}-600`} />
                      </div>
                      <span className="font-medium text-ink">{cat.label}</span>
                    </div>
                    <span className="text-lg font-semibold text-ink">
                      {stats?.by_category?.[cat.key] || 0}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-white border border-ink-200 shadow-sm mt-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-ink font-display">
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/renewals" className="block">
                  <Button variant="outline" className="w-full justify-start hover:bg-paper hover:text-ink">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Renewal
                  </Button>
                </Link>
                <Link to="/calendar" className="block">
                  <Button variant="outline" className="w-full justify-start hover:bg-paper hover:text-ink">
                    <Calendar className="w-4 h-4 mr-2" />
                    View Calendar
                  </Button>
                </Link>
                <Link to="/settings" className="block">
                  <Button variant="outline" className="w-full justify-start hover:bg-paper hover:text-ink">
                    <Clock className="w-4 h-4 mr-2" />
                    Notification Settings
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
